import { useEffect, useState } from 'react'
import type { IslandProps } from '../registry'
import { useBookings } from './useBookings'
import {
  BookingsTable,
  BookingsTableSkeleton,
  BookingsErrorState,
  BookingsEmptyState,
} from './BookingsTable'
import { BookMeSettingsCard } from '../bookme-settings/SettingsCard'
import { supabase } from '../../lib/supabase'
import { toast } from '../../lib/toast'
import { BOOKME_ONBOARDING_URL } from '../../lib/product'

export const TURN_ON_URL = BOOKME_ONBOARDING_URL

export default function BookingsIsland({ user }: IslandProps) {
  const { rows, status, error, refetch } = useBookings(user?.id ?? null)
  const [slug, setSlug] = useState<string>('')

  useEffect(() => {
    if (!user?.id) {
      setSlug('')
      return
    }
    let cancelled = false
    void (async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('setting_value')
        .eq('user_id', user.id)
        .eq('setting_key', 'booking_slug')
        .maybeSingle()
      if (cancelled) return
      const s = (data as { setting_value?: string } | null)?.setting_value?.trim() ?? ''
      setSlug(s)
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const bookingUrl = slug ? `${origin}/book/${slug}` : null

  function handleCopy() {
    if (!bookingUrl) return
    void navigator.clipboard
      .writeText(bookingUrl)
      .then(() => toast('Booking link copied', 'success'))
      .catch(() => toast('Could not copy link', 'error'))
  }

  // No handle yet: the setup wizard turns the booking page on and lands back
  // on /meet/#calendar (frontend-simplification Phase 4). The settings card
  // above stays available for setting it up in place.
  function handleTurnOn() {
    window.location.assign(TURN_ON_URL)
  }

  return (
    <>
      <div id="bookme-settings-anchor" />
      {user?.id && <BookMeSettingsCard userId={user.id} onSlugChange={setSlug} />}

      <h2 style={{ margin: '0 0 12px', fontSize: 'var(--text-md)', fontWeight: 600 }}>Bookings</h2>
      {status === 'idle' && <BookingsTableSkeleton />}
      {status === 'loading' && <BookingsTableSkeleton />}
      {status === 'error' && <BookingsErrorState error={error} onRetry={refetch} />}
      {status === 'success' && rows.length === 0 && (
        <BookingsEmptyState
          bookingUrl={bookingUrl}
          onCopy={handleCopy}
          onOpenSettings={handleTurnOn}
        />
      )}
      {status === 'success' && rows.length > 0 && <BookingsTable rows={rows} />}
    </>
  )
}
